<template>
  <v-container
    fluid
  >
    <v-row justify="center">
      <v-col
        cols="12"
        md="10"
      >
        <base-material-card
          color="green"
          :title="cardTitle"
        >
          <v-form ref="form">
            <v-container class="py-0">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="user.firstName"
                    :label="$t('Components.Forms.UserDetails.Labels.FirstNameLabel')"
                    :rules="[firstNameValidation]"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="user.lastName"
                    class="purple-input"
                    :label="$t('Components.Forms.UserDetails.Labels.LastNameLabel')"
                    :rules="[lastNameValidation]"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="user.email"
                    :label="$t('Components.Forms.UserDetails.Labels.EmailAddressLabel')"
                    class="purple-input"
                    :rules="[emailValidation]"
                    :readonly="allowEditingEmail ? false : true"
                  />
                </v-col>
                <v-col
                  v-if="isNewUser"
                  cols="12"
                >
                  <v-autocomplete
                    v-model="user.resellerUid"
                    color="primary"
                    item-color="primary"
                    :label="$t('Components.Forms.UserDetails.Labels.ResellerLabel')"
                    :items="resellers"
                    item-text="name"
                    item-value="uid"
                    :rules="[resellerUidValidation]"
                  />
                </v-col>
                <v-col
                  cols="12"
                >
                  <v-autocomplete
                    v-model="user.role"
                    color="primary"
                    item-color="primary"
                    :label="$t('Components.Forms.UserDetails.Labels.RoleLabel')"
                    :items="roles"
                    item-text="roleName"
                    item-value="roleName"
                    :rules="[roleValidation]"
                  />
                </v-col>
                <v-col
                  v-if="isNewUser"
                  cols="6"
                >
                  <v-text-field
                    v-model="user.password"
                    label="Password*"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    color="primary"
                    :disabled="isSavedUser"
                    :rules="[passwordValidation]"
                    @click:append.prevent="showPassword = !showPassword"
                  />
                </v-col>
                <v-col
                  v-if="isNewUser"
                  cols="6"
                >
                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password*"
                    :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    color="primary"
                    :disabled="isSavedUser"
                    :rules="[confirmPasswordValidation]"
                    @click:append.prevent="showConfirmPassword = !showConfirmPassword"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
          <template v-slot:actions>
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="auto"
                class="mr-auto"
              >
                <v-icon
                  v-if="showRouterReturnLink"
                  class="mr-1"
                >
                  mdi-menu-left
                </v-icon>
                <router-link
                  v-if="showRouterReturnLink"
                  :to="{ name: 'Users', }"
                >
                  <span>All users</span>
                </router-link>
              </v-col>
              <v-col cols="auto" v-if="allowSaving">
                <debounce-v-btn
                  class="font-weight-light"
                  color="success"
                  loading:loading
                  @click="Save()"
                >
                  Save
                </debounce-v-btn>
                <debounce-v-btn
                  class="font-weight-light"
                  color="success"
                  loading:loading
                  @click="Save(true)"
                >
                  Save & close
                </debounce-v-btn>
                <v-btn
                  class="font-weight-light v-btn--tight"
                  color="warning"
                  loading:loading.commit
                  @click="$emit('closeUserForm')"
                >
                  Discard changes
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import validation from '@/helpers/validationHelper'

  export default {
    name: 'UserForm',
    props: {
      cardTitle: {
        type: String,
        required: true,
      },
      user: {
        type: Object,
        required: false,
        default: null,
      },
      isNewUser: {
        type: Boolean,
        required: false,
        default: false,
      },
      showRouterReturnLink: {
        type: Boolean,
        required: false,
        default: true,
      },
      loading: {
        type: Boolean,
        required: false,
        default: false,
      },
      badRequestReasons: {
        type: Object,
        required: false,
        default: null,
      },
      allowSaving:{
        type: Boolean,
        required: false,
        default: true
      },
      allowEditingEmail:{
        type: Boolean,
        required: false,
        default: true
      },
    },
    data: () => ({
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
    }),
    computed: {
      resellers () {
        return this.$store.state.resellers.list
      },
      roles () {
        return this.$store.state.roles.list
      },
      isSavedUser () {
        return this.user.uid !== ''
      },
    },
    watch: {
      badRequestReasons () {
        if (this.badRequestReasons) {
          // trigger the rules so the api errors are shown
          this.$refs.form.validate()
        }
      },
    },
    created: async function () {
      if (this.resellers.length < 1 && this.isNewUser) {
        await this.$store.dispatch('RefreshResellers')
      }
      if (this.roles.length < 1 ) {
        await this.$store.dispatch('RefreshRoles')
      }
    },
    mounted: function () {
    },
    methods: {
      async Save (shouldClose) {
        if (this.$refs.form.validate() !== true) {
          return false
        }
        var dispatchEvent = shouldClose ? 'saveAndCloseUserForm' : 'saveUser'
        this.$emit(dispatchEvent, this.user)
      },
      passwordValidation (value) {
        if (validation.stringIsNullOrWhitespace(value)) {
          return 'Password is required'
        }
        if (value !== this.confirmPassword) {
          return 'Passwords must match'
        }
        return true
      },
      firstNameValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value) || this.$t('Components.Forms.UserDetails.Validation.RequiredFirstName')
      },
      lastNameValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value) || this.$t('Components.Forms.UserDetails.Validation.RequiredLastName')
      },
      emailValidation (value) {
        if (!validation.stringIsNotNullOrWhitespace(value)) {
          return this.$t('Components.Forms.UserDetails.Validation.RequiredEmailAddress')
        }
        if (!validation.stringIsEmail(value)) {
          return this.$t('Components.Forms.UserDetails.Validation.InvalidEmailAddress')
        }
        if (this.badRequestReasons && this.badRequestReasons.Email) {
          return this.$t('Components.Forms.UserDetails.Validation.BadRequestReasons.' + this.badRequestReasons.Email[0])
        }
        return true
      },
      resellerUidValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value) || this.$t('Components.Forms.UserDetails.Validation.RequiredReseller')
      },
      roleValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value)
      },
      confirmPasswordValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value) || 'Confirm Password is required'
      },
    },
  }
</script>
